import { useMemo } from 'react';

import { Modal } from '@/core/components/Modal/Modal';
import { OverlayStyle } from '@/core/components/Modal/Modal.types';
import { Icon } from '@/core/components/Icons';
import { useTopmostZIndex } from '@/core/hooks/useTopmostZIndex';

import { DisplaySourceMedia, formatSourcePublishedDate } from '@/features/Reports/helpers';
import { buildMediaProxyUrl } from '@/features/Reports/media-proxy';

import { MediaPlayer } from './MediaPlayer';
import {
  CloseButton,
  FileInfo,
  FileName,
  InfoHeading,
  InfoLabel,
  InfoRow,
  InfoRows,
  InfoValue,
  MetadataSection,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  PreviewImage,
  PreviewWrapper,
} from './MediaPreviewModal.styles';
import { MediaPreviewModalProps } from './MediaPreviewModal.types';

const getMediaTypeLabel = (mediaType: DisplaySourceMedia['resolvedMedia']['mediaType']) =>
  mediaType === 'video' ? 'Video' : 'Image';

export const MediaPreviewModal = ({ media, isOpen, onClose }: MediaPreviewModalProps) => {
  const zIndex = useTopmostZIndex(isOpen);
  const modalTitle = media
    ? `${media.resolvedMedia.mediaType === 'video' ? 'VIDEO' : 'IMAGE'} MEDIA`
    : '';
  const fileName = media?.resolvedMedia.title ?? media?.title ?? '';

  const dateCreated = useMemo(
    () => (media?.publishedDatetime ? formatSourcePublishedDate(media.publishedDatetime) : null),
    [media?.publishedDatetime]
  );

  if (!media) {
    return null;
  }

  const imageSrc = buildMediaProxyUrl(
    media.resolvedMedia.playbackUrl ?? media.resolvedMedia.thumbnailUrl
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      overlayStyle={OverlayStyle.BLUR}
      zIndex={zIndex}
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{modalTitle}</ModalTitle>
          <CloseButton
            type="button"
            onClick={onClose}
            aria-label="Close modal"
          >
            <Icon
              name="Close"
              size={14}
            />
          </CloseButton>
        </ModalHeader>
        <PreviewWrapper>
          {media.resolvedMedia.mediaType === 'image' ? (
            imageSrc && (
              <PreviewImage
                src={imageSrc}
                alt={fileName}
              />
            )
          ) : (
            <MediaPlayer
              media={media.resolvedMedia}
              alt={fileName}
              sourceUrl={media.sourceUrl}
            />
          )}
        </PreviewWrapper>
        <MetadataSection>
          <div>
            <FileName>{fileName}</FileName>
            <FileInfo>{`${getMediaTypeLabel(media.resolvedMedia.mediaType)} · ${media.name}`}</FileInfo>
          </div>
          {dateCreated && (
            <>
              <InfoHeading>Informations</InfoHeading>
              <InfoRows>
                <InfoRow>
                  <InfoLabel>Date created</InfoLabel>
                  <InfoValue>{dateCreated}</InfoValue>
                </InfoRow>
              </InfoRows>
            </>
          )}
        </MetadataSection>
      </ModalContainer>
    </Modal>
  );
};
